import React, { useContext, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { Login } from "./auth/Login";
import { SearchResultsProvider } from "./context/SearchResultsContext";

import Tasks from "./pages/Tasks/Tasks";
import { TaskForm } from "./forms/TaskForm/TaskForm";
import { Labels } from "./pages/Labels/Labels";
import { Notes } from "./pages/Notes/Notes";
import CaseDetails from "./pages/CaseDetails/CaseDetails";
import { Dashboard } from "./pages/Dashboard/Dashboard";
import { ThemeProvider, ThemeContext } from "./context/ThemeContext";
import Settings from "./pages/Settings/Settings";
import { Cases } from "./pages/Cases/Cases";
import { ToastProvider, useToast } from "./context/ToastContext";
import { LoadingProvider } from "./context/LoadingContext";
import gsap from "gsap";
import { NoteForm } from "./forms/NoteForm/NoteForm";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PageNotFound } from "./pages/PageNotFound/PageNotFound";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AddUsers } from "./pages/AddUsers/AddUsers";
import { UserForm } from "./forms/UserForm/UserForm";
import { Branch } from "./pages/Branch/Branch";
import { BranchDetails } from "./pages/BranchDetails/BranchDetails";
import { MyClients } from "./pages/MyClients/MyClients";
import { MyClientForm } from "./forms/MyClientForm/MyClientForm";
import { Timeline } from "./pages/Timeline/Timeline";
import { Expenses } from "./pages/Expenses/Expenses";
import { SuperAdminModule } from "./pages/SuperAdminModule/SuperAdminModule";
import { SAPlanForm } from "./forms/SAPlanForm/SAPlanForm";
import { SAUserForm } from "./forms/SAUserForm/SAUserForm";
import { SACompanyForm } from "./forms/SACompanyForm/SACompanyForm";
import { Invoices } from "./pages/Invoices/Invoices";
import { DisplayBoard } from "./pages/DisplayBoard/DisplayBoard";
import { GlobalSettingsProvider } from "./context/GlobalSettingsContext";
import UserDetailPage from "./pages/UserDetailsPage/UserDetailsPage";
import PlanDetailsPage from "./pages/PlanDetailsPage/PlanDetailsPage";
import CompanyDetailsPage from "./pages/CompanyDetailsPage/CompanyDetailsPage";
import ExtendedViewPage from "./pages/ExtendedViewPage/ExtendedViewPage";
import Profile from "./pages/Profile/Profile";
import GlobalSettings from "./pages/GlobalSettingsPage/GlobalSettingsPage";
import { TaskTabProvider } from "./context/TaskTabContext";
import { CaseSelectionProvider } from "./context/CaseSelectionContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ErrorPage } from "./pages/ErrorPage/ErrorPage";
import ViewTask from "./pages/ViewTask/ViewTask";
import ExpenseView from "./pages/ExpenseView/ExpenseView";
import ViewMyClient from "./pages/ViewMyClient/ViewMyClient";
import ViewNote from "./pages/ViewNote/ViewNote";
import { ViewAdditionalDoc } from "./pages/ViewAdditionalDoc/ViewAdditionalDoc";
import { BubbleSettingsProvider } from "./context/BubbleSettingsContext";
import { ViewLabel } from "./pages/ViewLabel/ViewLabel";
import { CaseForm } from "./forms/CaseForm/CaseForm";
import { BranchForm } from "./forms/BranchForm/BranchForm";
import { InvoiceForm } from "./forms/InvoiceForm/InvoiceForm";
import { LabelForm } from "./forms/LabelForm/LabelForm";
import { ExpenseForm } from "./forms/ExpenseForm/ExpenseForm";
import AddEditCourtDetailsForm from "./forms/AddEditCourtDetailsForm/AddEditCourtDetailsForm";
import { TaskSelectionProvider } from "./context/TaskSelectionContext";
import { InvoiceSelectionProvider } from "./context/InvoiceSelectionContext";
import { LabelSelectionProvider } from "./context/LabelSelectionContext";
import { NoteSelectionProvider } from "./context/NoteSelectionContext";
import { ExpenseSelectionProvider } from "./context/ExpenseSelectionContext";
import { UserSelectionProvider } from "./context/UserSelectionContext";
import { ClientSelectionProvider } from "./context/ClientSelectionContext";
import { CaseLimitProvider } from "./context/CaseLimitContext";
import { UploadProvider } from "./context/UploadContext";
import { CasePaginationProvider } from "./context/CasePaginationContext";
import { setAuthHandlers } from "./services/api/axios";
import Cookies from 'js-cookie';
import { getAllGlobalSettingsData } from "./services/api/GlobalSettingsApi";
import { StorageProvider } from "./context/StorageContext";
import { getCompanyDataByID } from "./services/api/CompanyApi";

async function fetchAndStoreGlobalSettings() {
  try {
    console.log("Fetching global settings for Super Admin login...");

    const response = await getAllGlobalSettingsData();

    if (response.data && response.data.result && response.data.result.length > 0) {
      const settingsData = {
        result: response.data.result,
        companyId: response.data.companyId
      };

      localStorage.setItem('globalSettings', JSON.stringify(settingsData));
      console.log("Global settings fetched and stored successfully:", settingsData);
      return settingsData;
    } else {
      console.log("No global settings found for this company");
      return null;
    }
  } catch (error) {
    console.error("Error fetching global settings:", error);
    return null;
  }
}

async function processSuperAdminPostMessage(data) {
  const { accessToken, refreshToken, email, name, role, visibility, userId, branchIds, companyId } = data;

  const userData = {
    name,
    email,
    mobile: "",
    userId,
    branchIds: branchIds || []
  };

  // Clear existing auth
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("company");
  localStorage.removeItem("role");
  localStorage.removeItem("visibility");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("branchIds");
  localStorage.removeItem("bubbleSettings");
  localStorage.removeItem("globalSettings");
  localStorage.removeItem("hasSearched");

  Cookies.remove('user');
  Cookies.remove('accessToken');
  Cookies.remove('company');
  Cookies.remove('role');
  Cookies.remove('visibility');
  Cookies.remove('bubbleSettings');
  Cookies.remove('globalSettings');
  Cookies.remove('hasSearched');

  // Store tokens first so API calls work
  localStorage.setItem("accessToken", accessToken);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("user", JSON.stringify(userData));
  localStorage.setItem("role", role);
  localStorage.setItem("isAuthenticated", "true");
  if (visibility) localStorage.setItem("visibility", JSON.stringify(visibility));
  if (branchIds && branchIds.length > 0) localStorage.setItem("branchIds", JSON.stringify(branchIds));

  Cookies.set('accessToken', accessToken, { expires: 7, secure: true, sameSite: 'Strict' });
  Cookies.set('user', JSON.stringify(userData), { expires: 7, secure: true, sameSite: 'Strict' });
  Cookies.set('role', role, { expires: 7, secure: true, sameSite: 'Strict' });
  if (visibility) Cookies.set('visibility', JSON.stringify(visibility), { expires: 7, secure: true, sameSite: 'Strict' });

  // Fetch company data securely using the access token
  try {
    const companyRes = await getCompanyDataByID(companyId);
    const company = companyRes.data.company;
    localStorage.setItem("company", JSON.stringify(company));
    Cookies.set('company', JSON.stringify(company), { expires: 7, secure: true, sameSite: 'Strict' });
  } catch (e) {
    console.error("Failed to fetch company data:", e);
  }

  await fetchAndStoreGlobalSettings();
}

function AppContent() {
  const { themeColor } = useContext(ThemeContext);
  const { logout } = useAuth();
  const { showToast } = useToast();
  const appRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    setAuthHandlers(logout, showToast);
  }, [logout, showToast]);

  useEffect(() => {
    if (appRef.current) {
      gsap.fromTo(
        appRef.current,
        { opacity: 0.7 },
        { opacity: 1, duration: 0.7, ease: "power1.out" }
      );
    }

    if (overlayRef.current) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        {
          opacity: 0.25,
          duration: 0.15,
          ease: "power1.inOut",
          onComplete: () =>
            gsap.to(overlayRef.current, {
              opacity: 0,
              duration: 0.7,
              ease: "power1.inOut",
            }),
        }
      );
    }
  }, [themeColor]);

  return (
    <div
      className="app-wrapper"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      <div className="theme-flash-overlay" ref={overlayRef}></div>
      <div className="app" ref={appRef} style={{ minHeight: "100vh" }}>
        <Router>
          <Routes>
            <Route path="/" element={<Login />} />
            {/* <Route path="/login" element={<Login />} /> */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            >
              <Route path="profile" element={<Profile />} />
              <Route path="displayboard" element={<DisplayBoard />} />
              <Route
                path="displayboard/extended-view"
                element={<ExtendedViewPage />}
              />
              <Route path="cases" element={<Cases />} />
              <Route
                path="cases/casedetails/:courtref"
                element={<CaseDetails />}
              />
              <Route
                path="cases/casedetails/:courtref/additonaldoc/:id"
                element={<ViewAdditionalDoc />}
              />
              <Route
                path="/home/cases/editcase/:courtType/:courtRef"
                element={<CaseForm />}
              />
              <Route path="cases/newcase" element={<CaseForm />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tasks/newtask" element={<TaskForm />} />
              <Route path="tasks/edit/:id" element={<TaskForm />} />
              <Route path="tasks/viewtask/:id" element={<ViewTask />} />
              <Route path="my-clients" element={<MyClients />} />
              <Route path="my-clients/add-client" element={<MyClientForm />} />
              <Route path="my-clients/edit/:id" element={<MyClientForm />} />
              <Route
                path="my-clients/viewmyclient/:id"
                element={<ViewMyClient />}
              />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/add" element={<InvoiceForm />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="expenses/newexpense" element={<ExpenseForm />} />
              <Route path="expenses/edit/:expId" element={<ExpenseForm />} />
              <Route
                path="expenses/viewexpense/:id"
                element={<ExpenseView />}
              />
              <Route path="timeline" element={<Timeline />} />
              <Route path="labels" element={<Labels />} />
              <Route path="labels/newlabel" element={<LabelForm />} />
              <Route path="labels/editlabel/:labelId" element={<LabelForm />} />
              <Route path="/home/labels/:id" element={<ViewLabel />} />
              <Route path="notes" element={<Notes />} />
              <Route path="notes/newnote" element={<NoteForm />} />
              <Route path="notes/edit-note/:id" element={<NoteForm />} />
              <Route path="notes/details/:id" element={<ViewNote />} />
              {/* <Route path="settings" element={<Settings />} /> */}
              <Route path="settings" element={<GlobalSettings />} />
              <Route path="addusers" element={<AddUsers />} />
              <Route path="addusers/:id" element={<UserDetailPage />} />
              <Route path="addusers/add-user" element={<UserForm />} />
              <Route path="addusers/edit-user/:userid" element={<UserForm />} />

              <Route path="branch" element={<Branch />} />
              <Route path="branch/add" element={<BranchForm />} />
              <Route path="branch/edit/:branchID" element={<BranchForm />} />
              <Route path="branch/view/:branchId" element={<BranchDetails />} />

              <Route path="error" element={<ErrorPage />} />

              {/* <Route path="branch/:branchId" element={<BranchDetails />} /> */}
              {/* <Route path="branch/add-branch" element={<BranchForm />} /> */}
              {/* <Route path="branch/edit-branch/:branchID" element={<BranchForm />} /> */}
              <Route path="superadmin" element={<SuperAdminModule />} />
              <Route path="superadmin/addplan" element={<SAPlanForm />} />
              <Route path="superadmin/editplan/:id" element={<SAPlanForm />} />
              <Route
                path="superadmin/plandetails/:id"
                element={<PlanDetailsPage />}
              />
              <Route
                path="superadmin/companydetails/:id"
                element={<CompanyDetailsPage />}
              />
              <Route path="superadmin/adduser" element={<SAUserForm />} />
              <Route path="superadmin/edituser/:id" element={<SAUserForm />} />
              <Route path="superadmin/addcompany" element={<SACompanyForm />} />
              <Route
                path="superadmin/editcompany/:id"
                element={<SACompanyForm />}
              />
              <Route
                path="superadmin/court-details/:id"
                element={<AddEditCourtDetailsForm />}
              />
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Router>
      </div>
    </div>
  );
}

function App() {
  const [isReady, setIsReady] = useState(() => !window.opener);

  useEffect(() => {
    if (!window.opener) return;

    const superadminOrigin = process.env.REACT_APP_SUPERADMIN_ORIGIN;

    const handleMessage = async (event) => {
      if (event.origin !== superadminOrigin) return;
      if (event.data?.type !== 'SA_AUTH_DATA') return;

      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);

      try {
        await processSuperAdminPostMessage(event.data.payload);
      } catch (e) {
        console.error("Super admin auth failed:", e);
      } finally {
        setIsReady(true);
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal to superadmin tab that we are ready to receive auth data
    window.opener.postMessage({ type: 'SA_AUTH_READY' }, superadminOrigin);

    // If superadmin tab never responds within 5s, proceed normally
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      setIsReady(true);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Show loading only for Super Admin login
  if (!isReady) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          flexDirection: "column",
          gap: "16px"
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid #e5e7eb",
            borderTop: "3px solid #1a1a1a",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }}
        />
        <p style={{ color: "#666", margin: 0 }}>Initializing...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AuthProvider>
      <StorageProvider>
        <CasePaginationProvider>
          <BubbleSettingsProvider>
            <GlobalSettingsProvider>
              <ToastProvider>
                <ThemeProvider>
                  <CaseLimitProvider>
                    <UploadProvider>
                      <SearchResultsProvider>
                        <InvoiceSelectionProvider>
                          <TaskTabProvider>
                            <TaskSelectionProvider>
                              <LabelSelectionProvider>
                                <NoteSelectionProvider>
                                  <UserSelectionProvider>
                                    <ExpenseSelectionProvider>
                                      <ClientSelectionProvider>
                                        <CaseSelectionProvider>
                                          <LoadingProvider>
                                            <ErrorBoundary fallback={<ErrorPage />}>
                                              <AppContent />
                                            </ErrorBoundary>
                                          </LoadingProvider>
                                        </CaseSelectionProvider>
                                      </ClientSelectionProvider>
                                    </ExpenseSelectionProvider>
                                  </UserSelectionProvider>
                                </NoteSelectionProvider>
                              </LabelSelectionProvider>
                            </TaskSelectionProvider>
                          </TaskTabProvider>
                        </InvoiceSelectionProvider>
                      </SearchResultsProvider>
                    </UploadProvider>
                  </CaseLimitProvider>
                </ThemeProvider>
              </ToastProvider>
            </GlobalSettingsProvider>
          </BubbleSettingsProvider>
        </CasePaginationProvider>
      </StorageProvider>
    </AuthProvider>
  );
}

export default App;